import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { RowSelectionState } from '@tanstack/react-table';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useAdminList } from '@/lib/useAdminList';
import { ListFooter } from '@/components/ListFooter';
import { Plus, Search } from 'lucide-react';

interface Row {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  cityId: string | null;
  sportTypes: string[];
  status: string;
  activityCount: number;
  createdAt: string;
}

export function VenuesListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cityId, setCityId] = useState<string>('');
  const [sport, setSport] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
  const qc = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const {
    rows, total, loaded, isLoading,
    hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useAdminList<Row>(
    ['admin', 'venues', debounced, cityId, sport],
    '/api/admin/venues',
    { q: debounced, city_id: cityId, sport },
  );

  const bulk = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ data: { deleted: number; blocked: { id: string; activityCount: number }[] } }>(
        '/api/admin/venues/bulk-delete', { ids, confirm: 'DELETE' },
      ),
    onSuccess: () => {
      setRowSelection({});
      qc.invalidateQueries({ queryKey: ['admin', 'venues'] });
    },
  });

  const columns: ColumnDef<Row>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select row"
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Название',
      cell: (c) => <span className="text-zinc-100 font-medium">{c.getValue() as string}</span>,
    },
    {
      accessorKey: 'address',
      header: 'Адрес',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? <span className="text-zinc-400 text-xs">{v}</span> : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'city',
      header: 'Город',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? v : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'sportTypes', header: 'Спорты',
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          {(c.getValue() as string[]).map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Статус',
      cell: (c) => <StatusBadge status={c.getValue() as string} />,
    },
    {
      accessorKey: 'activityCount',
      header: 'Игр',
      cell: (c) => <span className="font-mono tabular-nums text-zinc-300">{c.getValue() as number}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Создано',
      cell: (c) => (
        <span className="font-mono text-xs tabular-nums text-zinc-500">
          {new Date(c.getValue() as string).toLocaleDateString('ru')}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Площадки</h1>
          <span className="text-xs text-zinc-500">
            Найдено: <span className="font-mono tabular-nums text-zinc-300">{total}</span>
          </span>
        </div>
        <Link to={'/venues/new' as any}>
          <Button className="w-full sm:w-auto">
            <Plus className="h-3.5 w-3.5" />
            Создать
          </Button>
        </Link>
      </div>

      <div className="rounded-lg border border-zinc-800/80 bg-bg-2 p-3 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[16rem] max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по названию/адресу…" className="pl-9" />
        </div>
        <Input value={cityId} onChange={(e) => setCityId(e.target.value)} placeholder="city_id (e.g. tolyatti)" className="w-full sm:max-w-[14rem]" />
        <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="sport (e.g. volleyball)" className="w-full sm:max-w-[14rem]" />
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-[#ff5c35]/30 bg-[#ff5c35]/5 px-4 py-2 text-sm">
          <span className="text-zinc-200">
            Выбрано: <span className="font-mono tabular-nums">{selectedIds.length}</span>
          </span>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm">Удалить выбранные</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Удалить {selectedIds.length} площадок?</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-zinc-400">
                Площадки с привязанными играми будут пропущены.
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={bulk.isPending}
                  onClick={() => bulk.mutate(selectedIds)}
                >
                  Удалить
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {bulk.data && (
        <div className="text-xs text-zinc-500">
          Удалено: <span className="font-mono tabular-nums text-zinc-300">{bulk.data.data.deleted}</span>.
          {bulk.data.data.blocked.length > 0 && (
            <> Заблокировано (есть игры):{' '}
              <span className="font-mono">
                {bulk.data.data.blocked.map((b) => `${b.id.slice(0,8)} (${b.activityCount})`).join(', ')}
              </span>
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(r) => navigate({ to: '/venues/$id', params: { id: r.id } })}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(r) => r.id}
            empty="Площадок не найдено"
          />
          <ListFooter
            loaded={loaded}
            total={total}
            hasMore={!!hasNextPage}
            isLoading={isFetchingNextPage}
            onMore={() => void fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
